/**
 * Servicio de Sincronización
 * Sincroniza datos locales (IndexedDB) con el backend cuando son aprobados
 */

import storageService from './storageService';
import { postsAPI, datosAPI } from '../utils/api';

/**
 * Estados de sincronización
 */
export const SyncStatus = {
  LOCAL: 'local',           // Solo existe localmente
  PENDING: 'pending',       // Enviado a revisión, esperando aprobación
  APPROVED: 'approved',     // Aprobado en backend, pendiente de eliminar local
  SYNCED: 'synced'          // Sincronizado (eliminado de local)
};

/**
 * Sincroniza posts locales con el backend
 * Busca posts locales que fueron enviados y verifica si ya fueron aprobados
 */
export async function syncPosts(userId) {
  try {
    console.log('🔄 Iniciando sincronización de posts...');
    
    // Inicializar storage
    await storageService.init();
    
    // Obtener todos los posts locales del usuario
    const localPosts = await storageService.posts.obtenerTodos();
    const userPosts = localPosts.filter(post => 
      post.userId === userId && 
      post.syncStatus === SyncStatus.PENDING &&
      post.backendId // Solo posts que tienen ID del backend
    );
    
    console.log(`📝 Posts locales pendientes de sincronización: ${userPosts.length}`);
    
    let syncedCount = 0;
    
    for (const localPost of userPosts) {
      try {
        // Verificar si el post fue aprobado en el backend
        // Usamos el endpoint público de listado para verificar
        const response = await postsAPI.listar({ page: 1, limit: 1 });
        const allPublishedPosts = response.data.data || [];
        
        // Buscar si el post local está en los publicados
        const foundInPublished = allPublishedPosts.some(p => p.id === localPost.backendId);
        
        if (foundInPublished) {
          // El post fue aprobado y publicado
          console.log(`✅ Post aprobado y publicado: ${localPost.titulo}`);
          
          // Eliminar de IndexedDB local
          await storageService.posts.eliminar(localPost.id);
          syncedCount++;
          
          console.log(`🗑️ Post local eliminado (ID: ${localPost.id})`);
        }
      } catch (error) {
        // Si hay error, solo loguear en modo debug (no mostrar warnings)
        if (error.response?.status === 403) {
          // Esperado para usuario_registrado - no loguear
        } else if (error.response?.status === 404) {
          console.log(`🔄 Post ${localPost.backendId} no encontrado en backend (eliminado o pendiente)`);
          // NO eliminar el post local si es 404 - puede estar pendiente de aprobación
        } else {
          // Solo loguear errores inesperados en modo debug
          if (process.env.NODE_ENV === 'development') {
            console.debug(`Sincronización: Post ${localPost.backendId} aún no publicado`);
          }
        }
      }
    }
    
    console.log(`✅ Sincronización completada: ${syncedCount} posts eliminados de local`);
    return { success: true, syncedCount };
    
  } catch (error) {
    console.error('❌ Error en sincronización de posts:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sincroniza datos históricos locales con el backend
 */
export async function syncHistoricalData(userId) {
  try {
    console.log('🔄 Iniciando sincronización de datos históricos...');
    
    await storageService.init();
    
    const localData = await storageService.historicalData.obtenerTodos();
    const userData = localData.filter(dato => 
      dato.userId === userId && 
      dato.syncStatus === SyncStatus.PENDING &&
      dato.backendId
    );
    
    console.log(`📊 Datos históricos pendientes de sincronización: ${userData.length}`);
    
    let syncedCount = 0;
    
    for (const localDato of userData) {
      try {
        const response = await datosAPI.obtener(localDato.backendId);
        const backendDato = response.data.data;
        
        if (backendDato && backendDato.estado === 'publicado') {
          console.log(`✅ Dato histórico aprobado: ${backendDato.tema}`);
          await storageService.historicalData.eliminar(localDato.id);
          syncedCount++;
        }
      } catch (error) {
        if (error.response?.status === 404) {
          console.log(`⚠️ Dato histórico no encontrado (eliminado): ID ${localDato.backendId}`);
          await storageService.historicalData.eliminar(localDato.id);
          syncedCount++;
        } else {
          console.warn(`Error verificando dato ${localDato.backendId}:`, error.message);
        }
      }
    }
    
    console.log(`✅ Sincronización completada: ${syncedCount} datos eliminados de local`);
    return { success: true, syncedCount };
    
  } catch (error) {
    console.error('❌ Error en sincronización de datos históricos:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sincroniza todos los datos locales del usuario
 */
export async function syncAllUserData(userId) {
  console.log('🔄 Iniciando sincronización completa...');
  
  const results = {
    posts: await syncPosts(userId),
    historicalData: await syncHistoricalData(userId)
  };
  
  const totalSynced = (results.posts.syncedCount || 0) + (results.historicalData.syncedCount || 0);
  
  if (totalSynced > 0) {
    console.log(`✅ Sincronización total: ${totalSynced} items eliminados`);
  } else {
    console.log('ℹ️ No hay items pendientes de sincronización');
  }
  
  return results;
}

/**
 * Actualiza el estado de sincronización de un post local
 */
export async function updatePostSyncStatus(localId, syncStatus, backendId = null) {
  try {
    await storageService.init();
    const post = await storageService.posts.obtenerPorId(localId);
    
    if (post) {
      const updatedPost = {
        ...post,
        syncStatus,
        ...(backendId && { backendId }), // Agregar backendId si se proporciona
        updatedAt: new Date().toISOString()
      };
      
      await storageService.posts.actualizar(localId, updatedPost);
      console.log(`✅ Estado de sincronización actualizado: ${syncStatus}`);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error actualizando estado de sincronización:', error);
    return false;
  }
}

/**
 * Actualiza el estado de sincronización de un dato histórico local
 */
export async function updateHistoricalDataSyncStatus(localId, syncStatus, backendId = null) {
  try {
    await storageService.init();
    const dato = await storageService.historicalData.obtenerPorId(localId);
    
    if (dato) {
      const updatedDato = {
        ...dato,
        syncStatus,
        ...(backendId && { backendId }),
        updatedAt: new Date().toISOString()
      };
      
      await storageService.historicalData.actualizar(localId, updatedDato);
      console.log(`✅ Estado de sincronización actualizado: ${syncStatus}`);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error actualizando estado de sincronización:', error);
    return false;
  }
}

/**
 * Hook para ejecutar sincronización periódica
 * Se puede llamar cada vez que el usuario visita la página de posts/historicidad
 */
export function useSyncOnMount(userId) {
  if (!userId) return;
  
  // Ejecutar sincronización al montar
  syncAllUserData(userId)
    .then(results => {
      const totalSynced = (results.posts.syncedCount || 0) + (results.historicalData.syncedCount || 0);
      if (totalSynced > 0) {
        // Opcional: mostrar notificación al usuario
        console.log(`🎉 ${totalSynced} items fueron aprobados y están ahora publicados`);
      }
    })
    .catch(error => {
      console.error('Error en sincronización automática:', error);
    });
}

export default {
  syncPosts,
  syncHistoricalData,
  syncAllUserData,
  updatePostSyncStatus,
  updateHistoricalDataSyncStatus,
  useSyncOnMount,
  SyncStatus
};

